import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    ThemeProvider,
    Tooltip,
    Typography,
} from '@mui/material';
import { Edit, FormatListNumbered, PowerSettingsNew } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import tableThemeCustom from '../../form/tableThemeCustom';
import SequenciaConvenioDialog from './SequenciaConvenioDialog';
import * as ContaAPI from '../../../api/conta';

const CAMPOS = [
    ['descricao', 'Descrição', 120, 'Nome para identificar este convênio na conta.'],
    ['codigoConvenio', 'Código do convênio', 60, 'Informe somente o código confirmado pelo banco.'],
    ['codigoBeneficiario', 'Código do beneficiário', 60, 'Pode ser diferente do número da conta corrente.'],
    ['posto', 'Posto', 20, 'Preencha quando informado pelo banco.'],
    ['carteira', 'Carteira', 30, 'Carteira de cobrança contratada.'],
    ['variacaoCarteira', 'Variação da carteira', 30, 'Preencha quando aplicável ao convênio.'],
    ['modalidade', 'Modalidade', 60, 'Modalidade de cobrança informada pelo banco.'],
    ['observacao', 'Observação', 4000, 'Anotações do cadastro. Não informe senhas ou tokens.'],
];
const vazio = () => ({ ...Object.fromEntries(CAMPOS.map(([key]) => [key, ''])), ativo: false });
const mensagem = (error) => error?.response?.data?.message || 'Não foi possível concluir a operação. Tente novamente.';

export default function ConveniosPanel({ conta, bloqueado = false }) {
    const theme = tableThemeCustom();
    const [rows, setRows] = useState([]);
    const [sequencia, setSequencia] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');
    const [draft, setDraft] = useState(null);
    const [confirmacao, setConfirmacao] = useState(null);
    const [erroDialog, setErroDialog] = useState('');
    const [contaAtiva, setContaAtiva] = useState(false);
    const requisicao = useRef(0);
    const gravando = useRef(false);
    const uuid = conta?.uuid;
    const podeIncluir = localStorage.getItem('incluir') === 'true';
    const podeEditar = localStorage.getItem('editar') === 'true';
    const podeInativar = localStorage.getItem('cancelar') === 'true';

    const carregar = useCallback(async () => {
        if (!uuid) return;
        const atual = ++requisicao.current;
        setLoading(true);
        setErro('');
        try {
            const { data } = await ContaAPI.listarConvenios(uuid);
            if (atual === requisicao.current) {
                setRows(data.data || []);
                setContaAtiva(data.contaAtiva === true);
            }
        } catch (error) {
            if (atual === requisicao.current) {
                setErro(mensagem(error));
                setRows([]);
            }
        } finally {
            if (atual === requisicao.current) setLoading(false);
        }
    }, [uuid]);

    useEffect(() => {
        setRows([]);
        setSequencia(null);
        setDraft(null);
        setConfirmacao(null);
        setSucesso('');
        setErroDialog('');
        carregar();
        return () => {
            requisicao.current += 1;
        };
    }, [carregar]);

    const salvar = async () => {
        if (gravando.current || bloqueado || !contaAtiva || !(draft?.uuid ? podeEditar : podeIncluir)) return;
        if (!draft.descricao?.trim()) {
            setErroDialog('Informe a descrição do convênio.');
            return;
        }
        gravando.current = true;
        setSaving(true);
        setErroDialog('');
        setSucesso('');
        try {
            const response = draft.uuid
                ? await ContaAPI.editarConvenio(uuid, draft)
                : await ContaAPI.cadastrarConvenio(uuid, draft);
            setDraft(null);
            setSucesso(response.data.message);
            await carregar();
        } catch (error) {
            setErroDialog(mensagem(error));
        } finally {
            gravando.current = false;
            setSaving(false);
        }
    };
    const alterarSituacao = async () => {
        const row = confirmacao;
        if (!row || gravando.current || bloqueado || (row.ativo ? !podeInativar : !podeEditar) || (!row.ativo && !contaAtiva))
            return;
        gravando.current = true;
        setSaving(true);
        setErroDialog('');
        setSucesso('');
        try {
            const response = await ContaAPI.situacaoConvenio(uuid, { uuid: row.uuid, versao: row.versao, ativo: !row.ativo });
            setConfirmacao(null);
            setSucesso(response.data.message);
            await carregar();
        } catch (error) {
            setErroDialog(mensagem(error));
        } finally {
            gravando.current = false;
            setSaving(false);
        }
    };
    const columns = useMemo(
        () => [
            { accessorKey: 'descricao', header: 'Descrição' },
            { accessorKey: 'codigoConvenio', header: 'Convênio' },
            { accessorKey: 'codigoBeneficiario', header: 'Beneficiário' },
            { accessorKey: 'carteira', header: 'Carteira' },
            {
                accessorKey: 'ativo',
                header: 'Situação',
                Cell: ({ row }) => (
                    <Chip
                        size="small"
                        label={row.original.ativo ? 'Ativo' : 'Inativo'}
                        color={row.original.ativo ? 'success' : 'default'}
                    />
                ),
            },
        ],
        []
    );

    if (!uuid)
        return (
            <Alert severity="info" sx={{ mt: 3 }}>
                Salve a conta e abra sua edição para cadastrar os convênios de cobrança.
            </Alert>
        );
    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    mt: 3,
                    p: { xs: 1, sm: 2 },
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Convênios de cobrança
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Cadastro vinculado à conta {conta.descricao}. Ativar o cadastro não registra boletos nem envia cobranças ao
                    banco.
                </Typography>
                {bloqueado && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Existem alterações na identificação ou situação da conta. Salve a conta e reabra a edição antes de manter
                        os convênios.
                    </Alert>
                )}
                {!loading && !contaAtiva && !erro && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Conta inativa: os convênios podem ser consultados e inativados.
                    </Alert>
                )}
                {erro && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {erro}
                    </Alert>
                )}
                {sucesso && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {sucesso}
                    </Alert>
                )}
                <MaterialReactTable
                    columns={columns}
                    data={rows}
                    getRowId={(row) => row.uuid}
                    enableRowActions
                    positionActionsColumn="first"
                    displayColumnDefOptions={{ 'mrt-row-actions': { size: 136, minSize: 136, maxSize: 136 } }}
                    localization={MRT_Localization_PT_BR}
                    initialState={{ density: 'compact', pagination: { pageIndex: 0, pageSize: 5 } }}
                    state={{ isLoading: loading, showProgressBars: saving }}
                    renderTopToolbarCustomActions={() => (
                        <Stack direction="row" spacing={1}>
                            <Button
                                type="button"
                                variant="contained"
                                disabled={!podeIncluir || bloqueado || !contaAtiva || loading || saving || Boolean(erro)}
                                onClick={() => {
                                    setDraft(vazio());
                                    setErroDialog('');
                                }}
                            >
                                Novo convênio
                            </Button>
                            <Button type="button" disabled={loading || saving} onClick={carregar}>
                                Atualizar
                            </Button>
                        </Stack>
                    )}
                    renderRowActions={({ row }) => (
                        <Stack
                            direction="row"
                            sx={{
                                flexWrap: 'nowrap',
                                alignItems: 'center',
                                width: 120,
                                '& > span': { display: 'inline-flex', flexShrink: 0 },
                            }}
                        >
                            <Tooltip title="Sequência bancária">
                                <span>
                                    <IconButton
                                        type="button"
                                        aria-label="Consultar e configurar sequência bancária"
                                        disabled={bloqueado || loading || saving}
                                        onClick={() => setSequencia(row.original)}
                                    >
                                        <FormatListNumbered />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Editar convênio">
                                <span>
                                    <IconButton
                                        aria-label="Editar convênio"
                                        disabled={!podeEditar || bloqueado || !contaAtiva || loading || saving}
                                        onClick={() => {
                                            setDraft({ ...row.original });
                                            setErroDialog('');
                                        }}
                                    >
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={row.original.ativo ? 'Inativar convênio' : 'Ativar cadastro'}>
                                <span>
                                    <IconButton
                                        aria-label={row.original.ativo ? 'Inativar convênio' : 'Ativar cadastro'}
                                        disabled={
                                            bloqueado ||
                                            loading ||
                                            saving ||
                                            (row.original.ativo ? !podeInativar : !podeEditar || !contaAtiva)
                                        }
                                        onClick={() => {
                                            setConfirmacao(row.original);
                                            setErroDialog('');
                                        }}
                                    >
                                        <PowerSettingsNew />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    )}
                />
                {sequencia && (
                    <SequenciaConvenioDialog
                        key={`${uuid}:${sequencia.uuid}`}
                        conta={conta}
                        convenio={sequencia}
                        bloqueado={bloqueado}
                        onClose={() => {
                            setSequencia(null);
                            carregar();
                        }}
                    />
                )}
                <Dialog
                    open={Boolean(draft)}
                    onClose={() => {
                        if (!saving) setDraft(null);
                    }}
                    fullWidth
                    maxWidth="md"
                >
                    <DialogTitle>{draft?.uuid ? 'Editar convênio' : 'Novo convênio'}</DialogTitle>
                    <DialogContent dividers>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Preencha apenas os dados conhecidos. Campos bancários ainda não confirmados podem ficar vazios. Novos
                            cadastros ficam inativos.
                        </Alert>
                        {erroDialog && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {erroDialog}
                            </Alert>
                        )}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            {CAMPOS.map(([key, label, max, ajuda]) => (
                                <TextField
                                    key={key}
                                    name={key}
                                    label={label}
                                    title={ajuda}
                                    helperText={ajuda}
                                    required={key === 'descricao'}
                                    value={draft?.[key] ?? ''}
                                    disabled={saving || bloqueado}
                                    inputProps={{ maxLength: max }}
                                    multiline={key === 'observacao'}
                                    minRows={key === 'observacao' ? 3 : undefined}
                                    sx={key === 'observacao' ? { gridColumn: '1 / -1' } : undefined}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setDraft((prev) => ({ ...prev, [key]: value }));
                                    }}
                                />
                            ))}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button type="button" disabled={saving} onClick={() => setDraft(null)}>
                            Cancelar
                        </Button>
                        <Button type="button" variant="contained" disabled={saving || bloqueado || !contaAtiva} onClick={salvar}>
                            {saving ? 'Salvando...' : 'Salvar convênio'}
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={Boolean(confirmacao)}
                    onClose={() => {
                        if (!saving) setConfirmacao(null);
                    }}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle>{confirmacao?.ativo ? 'Inativar convênio' : 'Ativar cadastro'}</DialogTitle>
                    <DialogContent>
                        {erroDialog && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {erroDialog}
                            </Alert>
                        )}
                        <Typography>
                            Confirma {confirmacao?.ativo ? 'a inativação' : 'a ativação'} de “{confirmacao?.descricao}”?
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Esta ação altera apenas o cadastro. Não envia instruções ao banco nem cancela boletos.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button type="button" disabled={saving} onClick={() => setConfirmacao(null)}>
                            Voltar
                        </Button>
                        <Button type="button" variant="contained" disabled={saving || bloqueado} onClick={alterarSituacao}>
                            Confirmar
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
}
